import { ReactNode } from 'react';
import { DefaultValues } from 'react-hook-form';
import { z } from 'zod';

import { FormModal, SubmitProps } from '@/components/form-modal.js';
import { Button } from '@/components/ui/button.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import {
  buildNameLikeSelectOptions,
  buildUserSelectOptions,
  emptySelectOption,
} from '@/components/ui/multi-select-utils.js';
import { createStudentFeedback, getStudentFeedbacks, getStudentReviewNotes } from '@/lib/api-calls.js';
import { airServerErrorFrom } from '@/lib/error-handling.js';
import { formCreateStudentFeedbackSchema } from '@/lib/student-utils.js';
import { StudentFeedback } from '@airreading/common/data';
import { booleanFromString, booleanToString } from '@airreading/common';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CreateStudentFeedbackForm } from './create-student-feedback-form.js';

type Form = z.infer<typeof formCreateStudentFeedbackSchema>;

export interface CreateStudentFeedbackProps {
  studentId: string;
  from?: StudentFeedback;
  trigger?: ReactNode;
  onSuccess?: () => void;
}

export const CreateStudentFeedback = ({ studentId, from, trigger, onSuccess }: CreateStudentFeedbackProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { mutateAsync: createStudentFeedbackMutate } = useMutation({ mutationFn: createStudentFeedback });

  const onSubmit = async ({ formData, closeModal, reset }: SubmitProps<Form>) => {
    const { relevelNeeded, learningPath, startingLesson, reviewer, ...rest } = formData;
    return createStudentFeedbackMutate({
      ...rest,
      studentId,
      relevelNeeded: booleanFromString(relevelNeeded),
      reviewerId: reviewer.value,
      ...(startingLesson?.value ? { learningPathId: learningPath?.value, startingLessonId: startingLesson.value } : {}),
    })
      .then(() => {
        toast({ title: 'Review note created successfully' });
        onSuccess?.();
        closeModal();
        reset();
        queryClient.invalidateQueries({ queryKey: getStudentFeedbacks().queryKey });
        queryClient.invalidateQueries({ queryKey: getStudentReviewNotes(studentId).queryKey });
      })
      .catch((error) => {
        toast({
          title: airServerErrorFrom(error)?.userFacingMessage || 'Failed to create review note. Please try again.',
          variant: 'destructive',
        });
      });
  };

  const defaultValues: DefaultValues<Form> = {
    studentFeedbackIssue: from?.studentFeedbackIssue ?? undefined,
    relevelNeeded: typeof from?.relevelNeeded === 'boolean' ? booleanToString(from.relevelNeeded) : undefined,
    learningPath: from?.learningPath ? buildNameLikeSelectOptions(from.learningPath) : emptySelectOption,
    startingLesson: from?.startingLesson ? buildNameLikeSelectOptions(from.startingLesson) : emptySelectOption,
    reviewer: from?.reviewer ? buildUserSelectOptions(from.reviewer) : emptySelectOption,
    note: from?.note ?? undefined,
    status: from?.status ?? undefined,
  };

  return (
    <FormModal
      modalTitle="Add student review note"
      dialogContentProps={{ className: 'max-w-2xl' }}
      formSchema={formCreateStudentFeedbackSchema}
      onSubmit={onSubmit}
      trigger={trigger || <Button>Add note</Button>}
      defaultValues={defaultValues}
      confirmBtnLabel="Add"
      shouldResetOnCancel
    >
      {({ form }) => <CreateStudentFeedbackForm form={form} />}
    </FormModal>
  );
};
